import { useEffect } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, Clock, HeartHandshake, Info } from "lucide-react";
import { PublicLayout } from "@/components/PublicLayout";
import { usePageMeta } from "@/hooks/usePageMeta";
import { useAuth } from "@/context/AuthContext";
import { ARTICLES, findArticle } from "@/data/articles";
import { WriteAboutIt } from "@/components/WriteAboutIt";
import { ThisHelped } from "@/components/ThisHelped";
import { TOOLS } from "@/data/tools";

const toolsFor = (slug) => TOOLS.filter((t) => t.articles?.includes(slug));

const SITE = "https://mindfulmeyourcalmcompanion.com";

const Block = ({ b }) => {
  if (b.h) return <h2 className="text-xl sm:text-2xl font-bold mt-9 mb-3">{b.h}</h2>;
  if (b.p) return <p className="leading-relaxed mb-4">{b.p}</p>;
  if (b.tip) return <div className="mm-soft p-4 my-5 text-sm leading-relaxed border-l-4 border-mm-primary">{b.tip}</div>;
  const Tag = b.ol ? "ol" : "ul";
  return (
    <Tag className={`${b.ol ? "list-decimal" : "list-disc"} pl-6 space-y-1.5 mb-5 leading-relaxed marker:text-mm-primary`}>
      {(b.ol || b.ul).map((li) => <li key={li}>{li}</li>)}
    </Tag>
  );
};

// Structured data so search engines understand the page as an article
const useArticleJsonLd = (a) => {
  useEffect(() => {
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.text = JSON.stringify({ "@context": "https://schema.org", "@type": "Article", headline: a.title, description: a.description, image: a.image,
      author: { "@type": "Organization", name: "Mindful Me" }, publisher: { "@type": "Organization", name: "Mindful Me", logo: { "@type": "ImageObject", url: `${SITE}/icon-512.png` } },
      mainEntityOfPage: `${SITE}/articles/${a.slug}` });
    document.head.appendChild(s);
    return () => s.remove();
  }, [a]);
};

const ArticleBody = ({ a }) => {
  const { user } = useAuth();
  usePageMeta({ title: `${a.title} | Mindful Me`, description: a.description, path: `/articles/${a.slug}`, image: a.image });
  useArticleJsonLd(a);
  const idx = ARTICLES.indexOf(a);
  const more = [1, 2, 3].map((k) => ARTICLES[(idx + k) % ARTICLES.length]);
  return (
    <PublicLayout>
      <article className="max-w-3xl mx-auto px-5 sm:px-8 pt-10 pb-6 mm-fade-up" data-testid={`article-${a.slug}`}>
        <Link to="/articles" className="text-sm text-mm-muted hover:text-mm-primary inline-flex items-center gap-1 transition-colors" data-testid="article-back"><ArrowLeft size={14} /> All articles</Link>
        <h1 className="text-4xl sm:text-5xl font-extrabold mt-4 leading-tight" data-testid="article-title">{a.title}</h1>
        <div className="text-sm text-mm-muted mt-3 inline-flex items-center gap-1"><Clock size={13} /> {a.readMins} min read</div>
        <img src={a.image} alt={a.imageAlt} className="w-full aspect-[16/9] object-cover rounded-3xl mt-6" data-testid="article-image" />
        <div className="mt-8 text-base">{a.blocks.map((b) => <Block key={JSON.stringify(b).slice(0, 80)} b={b} />)}</div>

        <ThisHelped slug={a.slug} title={a.title} />
        {a.journal && <WriteAboutIt journal={a.journal} />}
        {toolsFor(a.slug).length > 0 && (
          <div className="mm-soft rounded-3xl p-5 mt-10" data-testid="article-tools">
            <div className="font-semibold">Try it now, free</div>
            <div className="flex flex-wrap gap-2 mt-3">{toolsFor(a.slug).map((t) => <Link key={t.slug} to={`/tools/${t.slug}`} className="mm-chip" data-testid={`article-tool-${t.slug}`}>{t.emoji} {t.name}</Link>)}</div>
          </div>
        )}
        <div className="mm-card p-6 mt-10 text-center" data-testid="article-cta">
          <div className="font-bold text-lg">Ready to try it?</div>
          <p className="text-sm text-mm-muted mt-1">Mindful Me puts these skills, plus check-ins, journaling and gentle insights, in one calm place.</p>
          <Link to={user?.has_paid ? a.cta.to : "/pricing"} className="mm-btn mt-4 inline-block" data-testid="article-cta-button">Try it in Mindful Me</Link>
        </div>

        <div className="mt-6 text-sm leading-relaxed space-y-3" data-testid="article-safety">
          <p className="flex gap-2"><HeartHandshake size={18} className="text-mm-primary shrink-0 mt-0.5" />
            <span>If you're in crisis or thinking about suicide, please call or text <a href="tel:988" className="font-semibold text-mm-primary">988</a> (US &amp; Canada) or your local emergency number. You don't have to go through this alone.</span></p>
          <p className="flex gap-2 text-mm-muted"><Info size={18} className="shrink-0 mt-0.5" />
            <span>This article is for general information and support only. It isn't medical advice and isn't a substitute for professional care.</span></p>
        </div>
      </article>
      <section className="max-w-6xl mx-auto px-5 sm:px-8 pt-8 pb-4" data-testid="article-more">
        <h2 className="text-xl font-bold mb-4">Keep reading</h2>
        <div className="grid gap-4 sm:grid-cols-3">
          {more.map((m) => (
            <Link key={m.slug} to={`/articles/${m.slug}`} className="mm-card p-4 hover:-translate-y-0.5 transition-transform" data-testid={`article-more-${m.slug}`}>
              <div className="font-semibold leading-snug">{m.title}</div>
              <div className="text-xs text-mm-muted mt-1">{m.readMins} min read</div>
            </Link>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
};

export default function Article() {
  const { slug } = useParams();
  const a = findArticle(slug);
  useEffect(() => { window.scrollTo(0, 0); }, [slug]);
  if (!a) return <Navigate to="/articles" replace />;
  return <ArticleBody key={a.slug} a={a} />;
}
